import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { Navigation } from '@/components/Navigation';
import { Footer } from '@/components/Footer';
import CanonicalTag from '@/components/CanonicalTag';
import aduRentalImage from '@/assets/adu-rental-california.jpg';

const CanIRentOutMyADUInCalifornia = () => {
  const [showCalendly, setShowCalendly] = useState(false);
  const openCalendlyModal = () => setShowCalendly(true);
  const closeCalendlyModal = () => setShowCalendly(false);

  return (
    <>
      <CanonicalTag url="https://www.udaadu.com/can-i-rent-out-my-adu-in-california" />
      
      {/* Calendly Modal */}
      {showCalendly && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="relative bg-white rounded-lg w-full max-w-4xl h-full max-h-[90vh]">
            <button 
              onClick={closeCalendlyModal} 
              className="absolute top-4 right-4 z-10 p-2 bg-white rounded-full shadow-md hover:bg-gray-100 transition-colors"
            >
              ✕
            </button>
            <iframe 
              src="https://calendly.com/udaadu?embed=true&hide_gdpr_banner=1" 
              width="100%" 
              height="100%" 
              frameBorder="0" 
              title="Schedule a meeting"
            />
          </div>
        </div>
      )}
      
      <div className="min-h-screen bg-background">
        <Navigation />
        
        <main className="container mx-auto px-4 py-8">
          {/* Hero Section */}
          <div className="mb-12">
            <img 
              src={aduRentalImage} 
              alt="California ADU rental property" 
              className="w-full h-64 md:h-96 object-cover rounded-lg mb-8"
            />
            
            <h1 className="text-4xl md:text-5xl font-bold mb-6" style={{ color: '#E4683B' }}>
              Can I Rent Out My ADU in California?
            </h1>
            
            <p className="text-lg md:text-xl text-white leading-relaxed mb-8">
              Learn whether you can rent out your Accessory Dwelling Unit (ADU) in California. 
              Find essential information on rental regulations, restrictions, tenant guidelines, 
              and effective practices to turn your ADU into a lucrative rental opportunity.
            </p>
          </div>

          {/* Content Sections */}
          <div className="space-y-12">
            <section>
              <h2 className="text-3xl font-bold mb-6" style={{ color: '#E4683B' }}>
                Understanding California ADU Rental Regulations
              </h2>
              <p className="text-white leading-relaxed mb-6">
                In California, homeowners are allowed to rent out their ADUs, but certain regulations 
                must be observed. Typically, rentals must comply with local housing laws, tenant 
                protections, and safety codes. Many California cities allow ADUs to be rented on a 
                long-term basis (generally 30 days or longer).
              </p>
            </section>

            <section>
              <h2 className="text-3xl font-bold mb-6" style={{ color: '#E4683B' }}>
                Common ADU Rental Restrictions
              </h2>
              <p className="text-white leading-relaxed mb-6">
                Some California municipalities restrict short-term rentals, such as Airbnb, limiting 
                ADUs to long-term leases only. Cities may also have specific occupancy limits, parking 
                requirements, or other zoning-related regulations.
              </p>
            </section>

            <section>
              <h3 className="text-2xl font-bold mb-6" style={{ color: '#E4683B' }}>
                Best Practices for Renting Out Your ADU
              </h3>
              <div className="text-white leading-relaxed space-y-4">
                <p>• Ensure your ADU meets local building and safety codes.</p>
                <p>• Understand and adhere to local tenant protection laws.</p>
                <p>• Use clear rental agreements to prevent disputes.</p>
                <p>• Regularly maintain and inspect your ADU to ensure tenant safety.</p>
              </div>
            </section>

            <section>
              <h2 className="text-3xl font-bold mb-6" style={{ color: '#E4683B' }}>
                Frequently Asked Questions
              </h2>
              <div className="text-white leading-relaxed space-y-6">
                <div>
                  <p className="font-semibold mb-2">Q: Can I Airbnb my ADU?</p>
                  <p>A: Many California cities prohibit short-term rentals under 30 days. Always check local ordinances.</p>
                </div>
                
                <div>
                  <p className="font-semibold mb-2">Q: Do I need a special permit to rent my ADU?</p>
                  <p>A: Typically, no special permit beyond standard occupancy certification is required, but confirm with local regulations.</p>
                </div>
                
                <div>
                  <p className="font-semibold mb-2">Q: Are there occupancy limits?</p>
                  <p>A: Yes, typically based on the size and amenities of your ADU. Check local regulations.</p>
                </div>
              </div>
            </section>
          </div>

          {/* Call-to-Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center mt-12">
            <Button 
              onClick={openCalendlyModal}
              className="bg-primary text-primary-foreground hover:bg-primary/90 px-8 py-3 text-lg"
            >
              Book & Schedule
            </Button>
            <Button 
              asChild
              variant="outline"
              className="border-primary text-primary hover:bg-primary hover:text-primary-foreground px-8 py-3 text-lg"
            >
              <Link to="/landing">Learn More</Link>
            </Button>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default CanIRentOutMyADUInCalifornia;